import { ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from "@angular/core";
import { AgGridColumn } from "@modules/common/models/ag-grid-column";
import { ChangeDetectorWrapper } from "@modules/common/models/change-detector-wrapper";
import { AGGridLoaderService } from "@modules/common/services/ag-grid-loader.service";
import * as moment from 'moment';

declare global {
	interface Window {
		agGrid: any;
	}
}

/**
 * A component that wraps an ag-grid grid.
 */
@Component({
	selector: 'app-ag-grid',
	templateUrl: './ag-grid.component.html',
	styleUrls: ['./ag-grid.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ChangeDetectorWrapper]
})
export class AgGridComponent implements OnChanges, OnDestroy {
	/**
	 * Our license key for using ag-grid.
	 */
	private static readonly LICENSE_KEY = 'Evaluation_License_Valid_Until_2_May_2018__MTUyNTIxNTYwMDAwMA==d28c879e6375efc8359d943382f12d02';

	/**
	 * The data that will be displayed in the grid.
	 */
	@Input()
	public data?: any[];

	/**
	 * The headers for the grid data.
	 */
	@Input()
	public headers?: AgGridColumn[];

	/**
	 * The height that the grid should be, in pixels. If 0, height is set to 'auto'.
	 */
	@Input()
	public height = 0;

	/**
	 * If true, the grid will be paginated (use pages to display the data).
	 * If false, the grid will use a vertical scroll bar.
	 */
	@Input()
	public pagination = true;

	/**
	 * The DOM element that will be converted into the grid.
	 */
	@ViewChild('grid', { static: true })
	private gridDomElement!: ElementRef;

	/**
	 * The object containing the options for the grid.
	 * Also used to access the ag-grid api once the grid is created.
	 */
	private gridOptions?: any;

	constructor(
        private _agGridLoaderService: AGGridLoaderService,
        private _changeDetectorWrapper: ChangeDetectorWrapper
	) {	}

	public ngOnChanges(changes: SimpleChanges): void {
		const dataChanges = changes['data'];
		const headersChanges = changes['headers'];

		//If the data or headers of the component are changed, reset the grid.
		if (dataChanges || headersChanges) {
			if (dataChanges) {
				this.data = dataChanges.currentValue;
			}
			if (headersChanges) {
				this.headers = headersChanges.currentValue;
			}

			this.initializeGrid();
		}
	}

	public ngOnDestroy(): void {
		this.destroyGrid();
	}

	/**
	 * Callback method to export the data from the grid into a file.
	 * @param fileBaseName The name to use for the file, without the file extension.
	 */
	public exportData(fileBaseName: string): void {
		if (this.gridOptions && this.gridOptions.api && this.gridOptions.api.exportDataAsCsv && typeof this.gridOptions.api.exportDataAsCsv === 'function') {
			this.gridOptions.api.exportDataAsCsv({
				fileName: fileBaseName + '.csv',
				processCellCallback: (params: any) => {
					if (params.column.colDef.type === 'dateColumn') {
						return AgGridComponent.buildDateTimeValueFormatter()(params);
					}
					else {
						return params.value;
					}
				}
			});
		}
	}

	/**
	 * Loads the ag-grid library and creates the grid, replacing any existing grid.
	 */
	private initializeGrid(): void {
		//Load the ag-grid library. When the ag-grid library has loaded, create the grid.
		this._agGridLoaderService.loadAGGrid().subscribe(() => {
			//Set the license key.
			window.agGrid.LicenseManager.setLicenseKey(AgGridComponent.LICENSE_KEY);

			//Destroy the existing grid, if there is one.
			this.destroyGrid();

			//Build the options for the grid.
			this.gridOptions = AgGridComponent.buildGridOptions(this.data, this.headers, this.pagination);

			//Add the grid to the DOM.
			new window.agGrid.Grid(this.gridDomElement.nativeElement, this.gridOptions);

			//Run change detection.
			this._changeDetectorWrapper.detectChanges();
		});
	}

	/**
	 * Destroys the current grid, if there is one.
	 */
	private destroyGrid(): void {
		if (this.gridOptions && this.gridOptions.api && this.gridOptions.api.destroy && typeof this.gridOptions.api.destroy === 'function') {
			//Destroy the grid.
			this.gridOptions.api.destroy();
			//Leave change detection to the caller, don't detect changes here.
		}
	}

	/**
	 * Creates a grid options object for the grid.
	 */
	private static buildGridOptions(data: any[] | undefined, headers: AgGridColumn[] | undefined, pagination: boolean): any {
		return {
			columnDefs: AgGridComponent.buildColumnDefs(headers),
			rowData: data || [],
			defaultExportParams: {
				suppressTextAsCDATA: true
			},
			defaultColDef: {
				// allow every column to be aggregated
				enableValue: true,
				// allow every column to be grouped
				enableRowGroup: true,
				// allow every column to be pivoted
				enablePivot: true
			},
			enableColResize: true,
			enableSorting: true,
			animateRows: true,
			suppressAggFuncInHeader: true,
			enableFilter: true,
			paginationAutoPageSize: pagination, //For now, if we are paginating, always use auto page size. We can change this later if we need more configurability.
			pagination: pagination,
			groupMultiAutoColumn: true,
			suppressExcelExport: true,
			columnTypes: {
				'dateColumn': {}
			}
		}
	}

	/**
	 * Creates the grid headers in ag-grid's columnDefs format.
	 */
	private static buildColumnDefs(headers: AgGridColumn[] | undefined): ColumnDef[] {
		return (headers || []).map<ColumnDef>(header => ({
			headerName: header.fieldName,
            field: header.fieldName,
			valueFormatter: AgGridComponent.buildValueFormatter(header),
			type: header.dataType == 'DateTime' ? 'dateColumn' : null
		}));
	}

	/**
	 * Creates the value formatter function for a ColumnDef.
	 */
	private static buildValueFormatter(header: AgGridColumn): ((data: any) => string) | null {
		if (header.dataType == 'DateTime') {
			return AgGridComponent.buildDateTimeValueFormatter();
		}

		return null;
	}

	/**
	 * Creates a function that formats DateTime values.
	 */
	private static buildDateTimeValueFormatter(): (data: any) => string {
		return (data: any) => data.value ?
			(new Date(data.value).getSeconds() > 0 ?
				moment(data.value).format('MM/DD/YYYY HH:mm:ss') :
				moment(data.value).format('MM/DD/YYYY')) :
			'';
	}
}

/**
 * A selection of ag-grid's column definition properties that we are using.
 * Property descriptions taken from ag-grid's documentation.
 */
interface ColumnDef {
	/**
	 * The field of the row to get the cells data from.
	 */
	field?: string,

	/**
	 * The name to render in the column header. If not specified and field is specified,
	 * the field name would be used as the header name.
	 */
	headerName?: string,

	/**
	 * A comma separated string or array of strings containing ColumnType keys
	 * which can be used as a template for a column.
	 */
	type?: string | null,

	/**
	 * Function or expression. Formats the value for display.
	 */
	valueFormatter?: ((data: any) => string) | null
}
